import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import {
  FaHome,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaClock,
  FaWhatsapp,
  FaSchool,
  FaBus,
  FaCalendarAlt,
  FaUserTie,
  FaBookOpen,
  FaUsers,
  FaGraduationCap,
  FaChild,
  FaHandshake
} from "react-icons/fa";
import BreadCrumbImage from "../assets/images/breadcrumb.png";
import { 
  ESTABLISH_YEAR, 
  CONTACT, 
  EMAIL, 
  ADDRESS,
  LOGO_LABEL,
  LOGO_SLOGAN
} from "../utils/constant";

const Contact = () => {
  const contactCards = [
    {
      icon: FaPhone,
      title: "Call Us",
      value: CONTACT,
      action: `tel:${CONTACT}`,
      gradient: "from-blue-500 via-blue-400 to-cyan-500",
      hoverGradient: "from-blue-600 via-blue-500 to-cyan-600",
      iconColor: "text-white",
      shape: "rounded-2xl",
      description: "Office: 8 AM - 4 PM",
      effect: "shine"
    },
    {
      icon: FaWhatsapp,
      title: "WhatsApp",
      value: CONTACT,
      action: `https://wa.me/${CONTACT.replace('+', '')}`,
      gradient: "from-emerald-500 via-emerald-400 to-teal-500",
      hoverGradient: "from-emerald-600 via-emerald-500 to-teal-600",
      iconColor: "text-white",
      shape: "rounded-3xl",
      description: "10 AM - 4 PM",
      effect: "pulse"
    },
    {
      icon: FaEnvelope,
      title: "Email Us",
      value: EMAIL,
      action: `mailto:${EMAIL}`,
      gradient: "from-purple-500 via-purple-400 to-pink-500",
      hoverGradient: "from-purple-600 via-purple-500 to-pink-600",
      iconColor: "text-white",
      shape: "rounded-2xl",
      description: "Response in 24 hours",
      effect: "float"
    },
    {
      icon: FaClock,
      title: "Office Hours",
      value: "Mon-Sat: 8AM-4PM",
      action: "#",
      gradient: "from-amber-500 via-amber-400 to-orange-500",
      hoverGradient: "from-amber-600 via-amber-500 to-orange-600",
      iconColor: "text-white",
      shape: "rounded-3xl",
      description: "Sunday: Closed",
      effect: "scale"
    },
  ];

  const quickInfo = [
    {
      icon: FaSchool,
      title: "School Timing",
      content: "Pre to Class 10: 8:30 AM to 2:30 PM",
      note: "Summer/Winter timings vary",
      color: "from-blue-100 via-blue-50 to-white",
      border: "border-blue-200",
      shape: "rounded-xl"
    },
    {
      icon: FaBus,
      title: "Transport Service",
      content: "Available for all areas",
      note: "GPS buses with attendants",
      color: "from-emerald-100 via-emerald-50 to-white",
      border: "border-emerald-200",
      shape: "rounded-2xl"
    },
    {
      icon: FaCalendarAlt,
      title: "Admission Period",
      content: "December to March",
      note: "Upcoming academic session",
      color: "from-purple-100 via-purple-50 to-white",
      border: "border-purple-200",
      shape: "rounded-xl"
    },
    {
      icon: FaUserTie,
      title: "Principal Office",
      content: "By appointment only",
      note: "Meeting: 10 AM - 2 PM",
      color: "from-amber-100 via-amber-50 to-white",
      border: "border-amber-200",
      shape: "rounded-2xl"
    },
  ];

  const visitInfo = [
    {
      title: "Parents Meeting",
      description: "By prior appointment only",
      icon: FaUsers,
      color: "bg-gradient-to-br from-blue-50 to-blue-100",
      border: "border-blue-200",
      shape: "rounded-xl"
    },
    {
      title: "Office Visit",
      description: "During office hours only",
      icon: FaClock,
      color: "bg-gradient-to-br from-emerald-50 to-emerald-100",
      border: "border-emerald-200",
      shape: "rounded-2xl"
    },
    {
      title: "Admission Inquiry",
      description: "Bring student's documents",
      icon: FaGraduationCap,
      color: "bg-gradient-to-br from-purple-50 to-purple-100",
      border: "border-purple-200",
      shape: "rounded-xl"
    },
    {
      title: "Fee Payment",
      description: "Cash/Card/Cheque accepted",
      icon: FaHandshake,
      color: "bg-gradient-to-br from-amber-50 to-amber-100",
      border: "border-amber-200",
      shape: "rounded-2xl"
    },
    {
      title: "Student Admission",
      description: "Pre-Nursery to Class 10",
      icon: FaChild,
      color: "bg-gradient-to-br from-rose-50 to-rose-100",
      border: "border-rose-200",
      shape: "rounded-xl"
    },
    {
      title: "Documents Required",
      description: "Birth Certificate & Marksheet",
      icon: FaBookOpen,
      color: "bg-gradient-to-br from-indigo-50 to-indigo-100",
      border: "border-indigo-200",
      shape: "rounded-2xl"
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <Layout>
      <Helmet>
        <title>Contact Us | {LOGO_LABEL}</title>
        <meta
          name="description"
          content={`Contact ${LOGO_LABEL} - ${LOGO_SLOGAN}. Phone: ${CONTACT}, Email: ${EMAIL}, Address: ${ADDRESS}.`}
        />
      </Helmet>

      {/* Breadcrumb */}
      <div className="relative bg-gray-900">
        <div className="h-48 md:h-56">
          <img
            src={BreadCrumbImage}
            alt="Breadcrumb"
            className="object-cover w-full h-full opacity-60"
          />
          <div className="absolute inset-0 bg-black/50" />
        </div>

        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          <div className="flex items-center mb-3 text-sm text-white">
            <Link to="/" className="flex items-center transition-colors hover:text-gray-200">
              <FaHome className="w-4 h-4 mr-2" />
              Home
            </Link>
            <span className="mx-2">/</span>
            <span className="font-semibold">Contact Us</span>
          </div>
          <h1 className="text-3xl font-bold text-white md:text-4xl">
            Contact Us
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="container px-4 py-8 mx-auto md:py-12">
        {/* Introduction - Smaller font */}
        <motion.div 
          className="max-w-3xl mx-auto mb-8 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <p className="text-sm text-gray-700 md:text-base">
            Reach out to us for any inquiries about admissions, academics, or school facilities. 
            Our office staff is ready to assist you during working hours.
          </p>
        </motion.div>

        {/* Contact Cards */}
        <motion.div
          className="grid gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-4 md:mb-10"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
        >
          {contactCards.map((card, i) => (
            <motion.a
              key={i}
              href={card.action}
              target={card.action.startsWith('http') ? "_blank" : "_self"}
              rel="noopener noreferrer"
              variants={cardVariants}
              whileHover={{ 
                y: -6,
                scale: 1.02,
                transition: { type: "spring", stiffness: 300 }
              }}
              className={`relative ${card.shape} overflow-hidden p-5 shadow-md group`}
            >
              {/* Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} group-hover:${card.hoverGradient} transition-all duration-500`} />
              
              {/* Hover Effect Layer */}
              <div className="absolute inset-0 transition-opacity duration-500 opacity-0 bg-white/10 group-hover:opacity-100" />
              
              {/* Special Effect based on card */}
              {card.effect === 'shine' && (
                <div className="absolute inset-0 transition-transform duration-1000 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-full" />
              )}
              
              {card.effect === 'pulse' && (
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100">
                  <div className="absolute inset-0 m-2 rounded-full bg-white/20 animate-ping" />
                </div>
              )}
              
              {card.effect === 'float' && (
                <div className="absolute opacity-0 -inset-2 bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:opacity-100 animate-float" />
              )}

              <div className="relative z-10">
                {/* Icon */}
                <motion.div 
                  className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm mb-3 ${card.iconColor}`}
                  whileHover={{ rotate: 360, scale: 1.1 }}
                  transition={{ duration: 0.5 }}
                >
                  <card.icon className="w-7 h-7" />
                </motion.div>

                <h3 className="mb-1 text-base font-bold text-white">
                  {card.title}
                </h3>
                <p className="mb-1 text-base font-semibold text-white">{card.value}</p>
                <p className="text-xs text-white/90">{card.description}</p>
              </div>
            </motion.a>
          ))}
        </motion.div>

        {/* Quick Information */}
        <motion.div 
          className="mb-8 md:mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-8 h-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500" />
            <h3 className="text-lg font-bold text-center text-gray-900">
              Quick Information
            </h3>
            <div className="w-8 h-1 rounded-full bg-gradient-to-r from-cyan-500 to-blue-500" />
          </div>
          
          <motion.div 
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {quickInfo.map((info, i) => (
              <motion.div
                key={i}
                variants={cardVariants}
                whileHover={{ 
                  y: -4,
                  transition: { type: "spring", stiffness: 300 }
                }}
                className={`relative ${info.shape} p-4 border ${info.border} bg-gradient-to-br ${info.color} shadow-sm overflow-hidden group`}
              >
                <div className="relative">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <info.icon className={`w-5 h-5 ${
                        info.icon === FaSchool ? 'text-blue-600' : 
                        info.icon === FaBus ? 'text-emerald-600' : 
                        info.icon === FaCalendarAlt ? 'text-purple-600' : 
                        'text-amber-600'
                      }`} />
                    </div>
                    <h4 className="text-sm font-bold text-gray-900">{info.title}</h4>
                  </div>
                  <p className="mb-1 text-sm font-medium text-gray-900">{info.content}</p>
                  <p className="text-xs text-gray-600">{info.note}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* School Address & Map */}
        <motion.div 
          className="grid gap-6 mb-8 md:grid-cols-2 md:mb-10"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          {/* School Address Card */}
          <motion.div
            whileHover={{ y: -4 }}
            className="relative p-6 overflow-hidden border border-blue-200 shadow-md bg-gradient-to-br from-blue-50 via-white to-cyan-50 rounded-2xl"
          >
            <div className="relative z-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="p-2 shadow-md rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                  <FaMapMarkerAlt className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    School Address
                  </h3>
                  <p className="text-sm font-medium text-blue-600">{LOGO_LABEL}</p>
                </div>
              </div>
              
              <div className="p-3 mb-4 bg-white border border-gray-100 rounded-lg">
                <p className="text-sm font-medium text-gray-800">{ADDRESS}</p>
              </div>
              
              <div className="p-4 border border-blue-200 rounded-lg bg-gradient-to-r from-blue-100 to-cyan-100">
                <div className="flex items-center gap-2 mb-2">
                  <FaBookOpen className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-bold text-gray-900">About Our School</span>
                </div>
                <p className="text-xs text-gray-700">
                  Established in <strong className="text-blue-700">{ESTABLISH_YEAR}</strong>, {LOGO_LABEL} - {LOGO_SLOGAN} 
                  {` provides`} quality CBSE education from Pre-Nursery to Class 10.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Google Map */}
          <motion.div
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300 }}
            className="overflow-hidden border border-gray-300 shadow-lg rounded-2xl"
          >
            <iframe
              title="Gyan Rashmi Siksha Sadan Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3598.451966774866!2d84.46755407517715!3d25.589893577460227!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39929d11de7d02a5%3A0xb8d237c95b49513e!2sGyan%20Rashmi%20Shiksha%20Sadan%2C%20Sharda%20Nagar!5e0!3m2!1sen!2sin!4v1766329817817!5m2!1sen!2sin"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "320px" }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              className="rounded-2xl"
            />
          </motion.div>
        </motion.div>

        {/* Visit Information */}
        <motion.div 
          className="mb-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          <div className="mb-6 text-center">
            <h3 className="mb-2 text-lg font-bold text-gray-900">
              Visit Information
            </h3>
            <p className="max-w-2xl mx-auto text-sm text-gray-600">
              Important guidelines for visiting our school campus
            </p>
          </div>
          
          <motion.div 
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {visitInfo.map((info, i) => (
              <motion.div
                key={i}
                variants={cardVariants}
                whileHover={{ 
                  y: -4,
                  scale: 1.01,
                  transition: { type: "spring", stiffness: 300 }
                }}
                className={`relative ${info.shape} p-5 border ${info.border} ${info.color} shadow-sm overflow-hidden group`}
              >
                <div className="relative z-10">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="p-2 bg-white rounded-lg shadow-sm">
                      <info.icon className={`w-5 h-5 ${
                        info.color.includes('blue') ? 'text-blue-600' :
                        info.color.includes('emerald') ? 'text-emerald-600' :
                        info.color.includes('purple') ? 'text-purple-600' :
                        info.color.includes('amber') ? 'text-amber-600' :
                        info.color.includes('rose') ? 'text-rose-600' :
                        'text-indigo-600'
                      }`} />
                    </div>
                    <h4 className="text-sm font-bold text-gray-900">{info.title}</h4>
                  </div>
                  <p className="text-sm text-gray-700">{info.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-8px); }
        }
        
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </Layout>
  );
};

export default Contact;